import React, { useMemo, useState } from "react";
import {
  EuiBadge,
  EuiBasicTableColumn,
  EuiButton,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiInMemoryTable,
  EuiLink,
  EuiPanel,
  EuiSearchBarProps,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTitle,
  EuiToolTip,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { AnketaResourcesEnum } from "src/auth/types";
import { useOpsanioCan } from "../can";
import {
  buildableAppliances,
  OpsanioHardwareItem,
  OpsanioHardwareKind,
} from "src/auth";
import { HardwareRequestModal } from "./requestModal";
import {
  HardwareTicketModal,
  HardwareTicketTarget,
  useCanOpenHardwareTicket,
} from "./ticketModal";
import { useHardwareInventory } from "./useHardwareInventory";
import { useChartColors } from "../appliance/useChartColors";
import { formatCapacity, HARDWARE_KIND_ICON } from "../appliance/hardware";
import { HardwareAllocationBar } from "../appliance/overview/charts";

const i18n = "anketa.opsanio.overview.hardware";

/** NetBox status value → OUI badge color. */
const STATUS_COLOR: Record<string, string> = {
  active: "success",
  planned: "warning",
  staged: "warning",
  inventory: "hollow",
  offline: "danger",
  failed: "danger",
  decommissioning: "danger",
};

/**
 * Fleet hardware inventory.
 *
 * Shows the physical hardware behind the fleet - servers, switches and DAC
 * cables tracked in NetBox - split into what is assigned to an appliance and
 * what is still free in the spare pool. Every number comes from NetBox; kinds
 * NetBox could not answer for are called out as unknown rather than counted as
 * an empty pool.
 *
 * This is the whole content of the `opsanio > Hardware` page, so the counts, the
 * per-kind allocation chart and the device table are all shown at once - there
 * is nothing else on the page to compete with them.
 */
export const HardwareInventoryPanel = (): JSX.Element => {
  // The reorder request mails inside sales: opsanio/hardware · create.
  const canRequest = useOpsanioCan()(AnketaResourcesEnum.opsanioHardware, "create");
  const { translate } = useAnketaI18n();
  const colors = useChartColors();
  const { inventory, summary, loading, loaded, error, reload } =
    useHardwareInventory();

  const [requesting, setRequesting] = useState<boolean>(false);
  // Hardware case (Replacement / Inspection / Return) for one device.
  const canOpenCase = useCanOpenHardwareTicket();
  const [caseTarget, setCaseTarget] = useState<HardwareTicketTarget | null>(null);

  const items = inventory?.items ?? [];
  // Stable identity: the kind-filter memo below depends on this list.
  const failed = useMemo(() => inventory?.failed ?? [], [inventory]);
  const initialLoading = loading && !loaded;

  // How many complete appliances the free pool still covers - the figure the
  // reorder decision actually turns on.
  const buildable = useMemo(
    () => buildableAppliances(summary, failed),
    [summary, failed],
  );

  const kindLabel: Record<OpsanioHardwareKind, string> = {
    server: translate(`${i18n}.kind.server`, { defaultMessage: "Servers" }),
    switch: translate(`${i18n}.kind.switch`, { defaultMessage: "Switches" }),
    dac: translate(`${i18n}.kind.dac`, { defaultMessage: "DAC cables" }),
  };

  const inUseLabel = translate(`${i18n}.inUse`, { defaultMessage: "In use" });
  const freeLabel = translate(`${i18n}.free`, { defaultMessage: "Free" });

  const columns: Array<EuiBasicTableColumn<OpsanioHardwareItem>> = [
    {
      field: "name",
      name: translate(`${i18n}.column.device`, { defaultMessage: "Device" }),
      sortable: true,
      truncateText: true,
      render: (name: string, item: OpsanioHardwareItem) => (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiIcon type={HARDWARE_KIND_ICON[item.kind]} size="m" />
          </EuiFlexItem>
          <EuiFlexItem>
            {item.url ? (
              <EuiLink href={item.url} target="_blank" external>
                <strong>{name}</strong>
              </EuiLink>
            ) : (
              <EuiText size="s">
                <strong>{name}</strong>
              </EuiText>
            )}
            <EuiText size="xs" color="subdued">
              {item.serial
                ? `SN: ${item.serial}`
                : translate(`${i18n}.noSerial`, {
                    defaultMessage: "no serial number",
                  })}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      field: "kind",
      name: translate(`${i18n}.column.kind`, { defaultMessage: "Type" }),
      sortable: true,
      width: "120px",
      render: (kind: OpsanioHardwareKind) => (
        <EuiBadge color="hollow">{kindLabel[kind]}</EuiBadge>
      ),
    },
    {
      field: "model",
      name: translate(`${i18n}.column.model`, { defaultMessage: "Model" }),
      sortable: true,
      truncateText: true,
      render: (model: string, item: OpsanioHardwareItem) => (
        <>
          <EuiText size="s">{model || "-"}</EuiText>
          {item.manufacturer && (
            <EuiText size="xs" color="subdued">
              {item.manufacturer}
            </EuiText>
          )}
        </>
      ),
    },
    {
      name: translate(`${i18n}.column.specs`, { defaultMessage: "Specs" }),
      width: "170px",
      render: (item: OpsanioHardwareItem) => {
        if (
          item.cpuCores === null &&
          item.memoryGb === null &&
          item.storageGb === null
        ) {
          return (
            <EuiText size="xs" color="subdued">
              -
            </EuiText>
          );
        }
        return (
          <EuiText size="xs">
            {item.cpuCores !== null && <div>{item.cpuCores} Cores</div>}
            {item.memoryGb !== null && <div>{item.memoryGb} GiB RAM</div>}
            {item.storageGb !== null && (
              <div>{formatCapacity(item.storageGb)}</div>
            )}
          </EuiText>
        );
      },
    },
    {
      field: "site",
      name: translate(`${i18n}.column.site`, { defaultMessage: "Site" }),
      sortable: true,
      truncateText: true,
      render: (site: string | null, item: OpsanioHardwareItem) => (
        <>
          <EuiText size="s">{site ?? "-"}</EuiText>
          {item.tenant && (
            <EuiText size="xs" color="subdued">
              {item.tenant}
            </EuiText>
          )}
        </>
      ),
    },
    {
      field: "statusLabel",
      name: translate(`${i18n}.column.status`, { defaultMessage: "Status" }),
      sortable: true,
      width: "120px",
      render: (statusLabel: string, item: OpsanioHardwareItem) => (
        <EuiBadge color={STATUS_COLOR[item.status] ?? "default"}>
          {statusLabel || item.status || "-"}
        </EuiBadge>
      ),
    },
    {
      field: "inUse",
      name: translate(`${i18n}.column.allocation`, {
        defaultMessage: "Allocation",
      }),
      sortable: true,
      width: "120px",
      render: (inUse: boolean) => (
        <EuiBadge color={inUse ? "primary" : "success"}>
          {inUse ? inUseLabel : freeLabel}
        </EuiBadge>
      ),
    },
    ...(canOpenCase
      ? [
          {
            name: translate(`${i18n}.column.actions`, { defaultMessage: "Actions" }),
            width: "70px",
            actions: [
              {
                name: translate("anketa.opsanio.hardware.ticket.action", {
                  defaultMessage: "Hardware case",
                }),
                description: translate("anketa.opsanio.hardware.ticket.actionHint", {
                  defaultMessage: "Replacement, inspection or return - sets the device to failed in NetBox",
                }),
                icon: "wrench",
                type: "icon" as const,
                onClick: (item: OpsanioHardwareItem) =>
                  setCaseTarget({ deviceId: item.id, deviceName: item.name }),
              },
            ],
          } as EuiBasicTableColumn<OpsanioHardwareItem>,
        ]
      : []),
  ];

  // Kind filter options only list kinds NetBox actually answered for, so the
  // filter can't imply a category exists with zero devices when it is unknown.
  const availableKinds = useMemo<OpsanioHardwareKind[]>(
    () =>
      (["server", "switch", "dac"] as OpsanioHardwareKind[]).filter(
        (kind) => !failed.includes(kind),
      ),
    [failed],
  );

  const search: EuiSearchBarProps = {
    box: {
      placeholder: translate(`${i18n}.search.placeholder`, {
        defaultMessage: "Search by name, serial, model, site…",
      }),
      incremental: true,
    },
    filters: [
      {
        type: "field_value_selection" as const,
        field: "kind",
        name: translate(`${i18n}.column.kind`, { defaultMessage: "Type" }),
        multiSelect: "or" as const,
        options: availableKinds.map((kind) => ({
          value: kind,
          view: kindLabel[kind],
        })),
      },
      {
        type: "field_value_selection" as const,
        field: "inUse",
        name: translate(`${i18n}.column.allocation`, {
          defaultMessage: "Allocation",
        }),
        multiSelect: false,
        options: [
          { value: false, view: freeLabel },
          { value: true, view: inUseLabel },
        ],
      },
    ],
  };

  return (
    <EuiFlexGroup direction="column" gutterSize="l">
      {caseTarget && (
        <HardwareTicketModal
          target={caseTarget}
          onClose={() => setCaseTarget(null)}
          onDone={() => {
            setCaseTarget(null);
            reload();
          }}
        />
      )}
      {requesting && (
        <HardwareRequestModal
          summary={summary}
          failed={failed}
          onClose={() => setRequesting(false)}
        />
      )}

      {/*
        Buildable appliances: the free pool expressed as whole appliances, which
        is the unit hardware is actually ordered in. Sits above the per-kind
        numbers because "we can still build 2" is the decision; "17 free DACs"
        is the evidence.
      */}
      <EuiFlexItem>
        <EuiPanel hasBorder paddingSize="l">
          <EuiFlexGroup alignItems="center" gutterSize="l" wrap>
            <EuiFlexItem grow={false} style={{ minWidth: 200 }}>
              <EuiStat
                title={`${buildable.appliances}`}
                description={translate(`${i18n}.buildable.title`, {
                  defaultMessage: "Complete appliances buildable",
                })}
                titleSize="l"
                titleColor={
                  buildable.appliances > 0 ? "success" : "danger"
                }
                isLoading={initialLoading}
              />
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.buildable.hint`, {
                  defaultMessage: "from free hardware, limited by the scarcest part",
                })}
              </EuiText>
            </EuiFlexItem>
            {buildable.lines.map((line) => (
              <EuiFlexItem key={line.key} grow={false} style={{ minWidth: 150 }}>
                <EuiStat
                  title={
                    line.covers === null
                      ? translate(`${i18n}.buildable.untracked`, {
                          defaultMessage: "n/a",
                        })
                      : `${line.covers}`
                  }
                  description={translate(`${i18n}.buildable.line`, {
                    values: { label: line.label, per: line.perAppliance },
                    defaultMessage: "{{label}} ({{per}} per appliance)",
                  })}
                  titleSize="s"
                  titleColor={
                    line.covers === null
                      ? "subdued"
                      : buildable.limitedBy.includes(line.key)
                        ? "warning"
                        : "default"
                  }
                  isLoading={initialLoading}
                />
                <EuiText size="xs" color="subdued">
                  {line.free === null
                    ? translate(`${i18n}.buildable.notCounted`, {
                        defaultMessage: "not counted in NetBox",
                      })
                    : translate(`${i18n}.buildable.free`, {
                        values: { free: line.free },
                        defaultMessage: "{{free}} free",
                      })}
                </EuiText>
              </EuiFlexItem>
            ))}
          </EuiFlexGroup>
        </EuiPanel>
      </EuiFlexItem>

      <EuiFlexItem>
        <EuiPanel hasBorder paddingSize="l">
          <EuiFlexGroup
            alignItems="center"
            gutterSize="m"
            responsive={false}
            wrap
          >
            <EuiFlexItem grow={false}>
              <EuiIcon type="storage" size="l" color="primary" />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiTitle size="s">
                <h2>
                  {translate(`${i18n}.title`, {
                    defaultMessage: "Hardware inventory",
                  })}
                </h2>
              </EuiTitle>
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.subtitle`, {
                  values: {
                    free: summary.total.free,
                    total: summary.total.total,
                  },
                  defaultMessage:
                    "{{free}} of {{total}} NetBox devices are free",
                })}
              </EuiText>
            </EuiFlexItem>
            {canRequest && (
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                fill
                iconType="email"
                onClick={() => setRequesting(true)}
              >
                {translate(`${i18n}.request`, {
                  defaultMessage: "Request hardware",
                })}
              </EuiButton>
            </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                iconType="refresh"
                onClick={reload}
                isLoading={loading}
              >
                {translate(`${i18n}.refresh`, {
                  defaultMessage: "Refresh hardware",
                })}
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>

          {error && (
            <>
              <EuiSpacer size="m" />
              <EuiCallOut
                size="s"
                color="danger"
                iconType="alert"
                title={translate(`${i18n}.error.title`, {
                  defaultMessage: "NetBox hardware could not be loaded",
                })}
              >
                <p>{error}</p>
              </EuiCallOut>
            </>
          )}

          {failed.length > 0 && (
            <>
              <EuiSpacer size="m" />
              <EuiCallOut
                size="s"
                color="warning"
                iconType="questionInCircle"
                title={translate(`${i18n}.partial.title`, {
                  values: {
                    kinds: failed.map((kind) => kindLabel[kind]).join(", "),
                  },
                  defaultMessage:
                    "NetBox did not answer for: {{kinds}} - those counts are unknown, not zero",
                })}
              />
            </>
          )}

          <EuiHorizontalRule margin="m" />

          {/* Four decision-driving numbers: the pool total and what is free per kind. */}
          <EuiFlexGroup gutterSize="l" wrap>
            <EuiFlexItem style={{ minWidth: 150 }}>
              <EuiStat
                title={`${summary.total.total}`}
                description={translate(`${i18n}.kpi.total`, {
                  defaultMessage: "Devices in NetBox",
                })}
                titleSize="l"
                isLoading={initialLoading}
              />
            </EuiFlexItem>
            {(["server", "switch", "dac"] as OpsanioHardwareKind[]).map(
              (kind) => (
                <EuiFlexItem key={kind} style={{ minWidth: 150 }}>
                  <EuiStat
                    title={
                      failed.includes(kind)
                        ? translate(`${i18n}.unknown`, {
                            defaultMessage: "unknown",
                          })
                        : `${summary[kind].free}`
                    }
                    description={translate(`${i18n}.kpi.kindFree`, {
                      values: { kind: kindLabel[kind] },
                      defaultMessage: "{{kind}} free",
                    })}
                    titleSize="l"
                    titleColor={
                      failed.includes(kind)
                        ? "subdued"
                        : summary[kind].free > 0
                          ? "success"
                          : "warning"
                    }
                    isLoading={initialLoading}
                  />
                  <EuiText size="xs" color="subdued">
                    {failed.includes(kind)
                      ? translate(`${i18n}.kind.unknownHint`, {
                          defaultMessage: "NetBox lookup failed",
                        })
                      : summary[kind].failed > 0
                        ? translate(`${i18n}.kind.ofTotalFailed`, {
                            values: {
                              total: summary[kind].total,
                              failed: summary[kind].failed,
                            },
                            defaultMessage: "of {{total}} total · {{failed}} defective",
                          })
                        : translate(`${i18n}.kind.ofTotal`, {
                            values: { total: summary[kind].total },
                            defaultMessage: "of {{total}} total",
                          })}
                  </EuiText>
                </EuiFlexItem>
              ),
            )}
          </EuiFlexGroup>

          {/* Assigned vs. free across the whole pool, as one honest bar. */}
          <EuiSpacer size="m" />
          <div
            style={{
              display: "flex",
              height: 16,
              borderRadius: 3,
              overflow: "hidden",
            }}
            role="img"
            aria-label={translate(`${i18n}.bar.aria`, {
              values: {
                inUse: summary.total.inUse,
                free: summary.total.free,
                failed: summary.total.failed,
              },
              defaultMessage: "{{inUse}} assigned, {{free}} free, {{failed}} defective",
            })}
          >
            {summary.total.inUse > 0 && (
              <div
                style={{
                  flexGrow: summary.total.inUse,
                  backgroundColor: colors.primary,
                }}
              />
            )}
            {summary.total.free > 0 && (
              <div
                style={{
                  flexGrow: summary.total.free,
                  backgroundColor: colors.success,
                }}
              />
            )}
            {summary.total.failed > 0 && (
              <div
                style={{
                  flexGrow: summary.total.failed,
                  backgroundColor: colors.danger,
                }}
              />
            )}
          </div>
          <EuiSpacer size="s" />
          <EuiFlexGroup gutterSize="l" wrap responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup
                gutterSize="xs"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  <EuiIcon type="dot" color="primary" size="m" />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="xs">
                    {translate(`${i18n}.kpi.inUse`, {
                      defaultMessage: "Assigned to appliances",
                    })}{" "}
                    <strong>{summary.total.inUse}</strong>
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup
                gutterSize="xs"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  <EuiIcon type="dot" color="success" size="m" />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiToolTip
                    content={translate(`${i18n}.kpi.free.tooltip`, {
                      defaultMessage:
                        "Devices without the opsanioinuse tag and not failed - the pool the questionnaire pickers offer.",
                    })}
                  >
                    <EuiText size="xs">
                      {translate(`${i18n}.kpi.free`, {
                        defaultMessage: "Free hardware",
                      })}{" "}
                      <strong>{summary.total.free}</strong>
                    </EuiText>
                  </EuiToolTip>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup
                gutterSize="xs"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  <EuiIcon type="dot" color="danger" size="m" />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiToolTip
                    content={translate(`${i18n}.kpi.failed.tooltip`, {
                      defaultMessage:
                        "NetBox status failed (e.g. after a hardware case) - not free, not counted as assigned.",
                    })}
                  >
                    <EuiText size="xs">
                      {translate(`${i18n}.kpi.failed`, {
                        defaultMessage: "Defective hardware",
                      })}{" "}
                      <strong>{summary.total.failed}</strong>
                    </EuiText>
                  </EuiToolTip>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
      </EuiFlexItem>

      <EuiFlexItem>
        <EuiFlexGroup gutterSize="l" wrap>
          <EuiFlexItem style={{ minWidth: 300 }}>
            <HardwareAllocationBar
              summary={summary}
              colors={colors}
              loading={initialLoading}
              failed={failed}
            />
          </EuiFlexItem>
          <EuiFlexItem style={{ minWidth: 300 }}>
            <EuiPanel hasBorder paddingSize="m" style={{ height: "100%" }}>
              <EuiTitle size="xxs">
                <h4>
                  {translate(`${i18n}.pool.title`, {
                    defaultMessage: "Free hardware pool",
                  })}
                </h4>
              </EuiTitle>
              <EuiSpacer size="s" />
              <EuiFlexGroup gutterSize="l" wrap>
                {(["server", "switch", "dac"] as OpsanioHardwareKind[]).map(
                  (kind) => (
                    <EuiFlexItem key={kind} grow={false}>
                      <EuiStat
                        title={
                          failed.includes(kind)
                            ? translate(`${i18n}.unknown`, {
                                defaultMessage: "unknown",
                              })
                            : `${summary[kind].free}/${summary[kind].total}`
                        }
                        description={kindLabel[kind]}
                        titleSize="m"
                        titleColor={
                          failed.includes(kind)
                            ? "subdued"
                            : summary[kind].free > 0
                              ? "success"
                              : "warning"
                        }
                        isLoading={initialLoading}
                      />
                    </EuiFlexItem>
                  ),
                )}
              </EuiFlexGroup>
              <EuiSpacer size="s" />
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.pool.hint`, {
                  defaultMessage:
                    "Free means untagged in NetBox (no opsanioinuse) and not failed - exactly what the questionnaire hardware pickers can still select.",
                })}
              </EuiText>
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      <EuiFlexItem>
        <EuiPanel hasBorder paddingSize="m">
          <EuiInMemoryTable<OpsanioHardwareItem>
            items={items}
            columns={columns}
            search={search}
            loading={loading}
            sorting={{ sort: { field: "name", direction: "asc" } }}
            // `initialPageSize`, not `pageIndex`/`pageSize`: the latter puts
            // EuiInMemoryTable into controlled mode, where without an
            // `onTableChange` handler it pins itself back to page 0 / 10 rows
            // and the rows-per-page selector does nothing.
            pagination={{
              initialPageSize: 10,
              pageSizeOptions: [10, 25, 50],
            }}
            message={
              <EuiEmptyPrompt
                iconType="storage"
                title={
                  <h3>
                    {translate(`${i18n}.empty.title`, {
                      defaultMessage: "No hardware found",
                    })}
                  </h3>
                }
                body={
                  <p>
                    {translate(`${i18n}.empty.body`, {
                      defaultMessage:
                        "NetBox returned no devices tagged autonubil + opsaniohardware.",
                    })}
                  </p>
                }
                actions={
                  <EuiButton onClick={reload} iconType="refresh">
                    {translate("anketa.common.refresh", {
                      defaultMessage: "Refresh",
                    })}
                  </EuiButton>
                }
              />
            }
          />
        </EuiPanel>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

export default HardwareInventoryPanel;
